import React, { useContext, useEffect, useState } from 'react';
import { fromUnixTime } from 'date-fns';
import { format } from 'date-fns-tz';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import FavoriteIconRedesign from '@icons/FavoriteIconRedesign';
import { BodySmall, ButtonOutlineLarge, H3, LabelLarge } from '@styled';

// Helpers
import { GlobalModalContext } from '@context/globalModalContext';
import colors from '@general/colors';
import { getTimeRangeText } from '@general/dates';
import fonts from '@general/fonts';

export const useSessionDetailModal = ({
    id,
    title,
    sessionType,
    sessionStatus,
    sessionStartUnix,
    sessionEndUnix,
    sessionRoom,
    description,
    event,
    isFavorite,
    showFavoriteIcon,
    toggleFavorite,
}) => {
    const [, setIsModalOpen, setModalChildren, setModalOuterClass] = useContext(GlobalModalContext);

    useEffect(() => {
        setModalOuterClass('session-detail-modal');
    }, [setModalOuterClass]);

    const openModal = () => {
        setModalChildren(() => (
            <SessionDetailModal
                id={id}
                title={title}
                sessionType={sessionType}
                sessionStatus={sessionStatus}
                sessionStartUnix={sessionStartUnix}
                sessionEndUnix={sessionEndUnix}
                sessionRoom={sessionRoom}
                description={description}
                event={event}
                isFavorite={isFavorite}
                showFavoriteIcon={showFavoriteIcon}
                toggleFavorite={toggleFavorite}
                closeModal={() => setIsModalOpen(false)}
            />
        ));

        setIsModalOpen(true);
    };

    return { openModal };
};

const Description = styled.p`
    p {
        color: ${colors.secondary};
        font-family: ${fonts.sans};
        font-size: 21px;
        font-style: normal;
        font-weight: 400;
        letter-spacing: 0.05px;
        line-height: 28px;
        margin: 0;
    }
`;

const SessionDetailModal = ({
    title,
    sessionType,
    sessionStartUnix,
    sessionEndUnix,
    sessionRoom,
    description,
    isFavorite: initialIsFavorite,
    showFavoriteIcon,
    toggleFavorite,
}) => {
    const [isFavorite, setIsFavorite] = useState(initialIsFavorite);

    return (
        <div>
            {sessionType && sessionType.toUpperCase() !== 'SELECT SESSION TYPE' && (
                <LabelLarge className="text-primary">{sessionType}</LabelLarge>
            )}

            <H3 className="mt-2 mb-2rem">{title}</H3>

            <div className="mb-2rem">
                <BodySmall>{format(fromUnixTime(sessionStartUnix), 'EEEE, MMMM d')}</BodySmall>
                <BodySmall>{getTimeRangeText(fromUnixTime(sessionStartUnix), fromUnixTime(sessionEndUnix))}</BodySmall>
                <BodySmall>&nbsp;</BodySmall>
                {sessionRoom && <BodySmall>{sessionRoom}</BodySmall>}
            </div>

            <Description className="mb-2rem" dangerouslySetInnerHTML={{ __html: description }} />

            {showFavoriteIcon && (
                <div className="mb-2rem">
                    <ButtonOutlineLarge
                        className="align-items-center d-flex"
                        onClick={() => {
                            setIsFavorite(!isFavorite);
                            toggleFavorite();
                        }}
                    >
                        <FavoriteIconRedesign isFavorite={isFavorite} outlineColor={colors.azure} />
                        <span className="ml-0.625rem">
                            {isFavorite && <>Unsave</>}
                            {!isFavorite && <>Save</>}
                            &nbsp;Session
                        </span>
                    </ButtonOutlineLarge>
                </div>
            )}
        </div>
    );
};

SessionDetailModal.propTypes = {
    title: PropTypes.string,
    sessionType: PropTypes.string,
    sessionStartUnix: PropTypes.number,
    sessionEndUnix: PropTypes.number,
    sessionRoom: PropTypes.string,
    description: PropTypes.string,
    isFavorite: PropTypes.bool,
    showFavoriteIcon: PropTypes.bool,
    toggleFavorite: PropTypes.func,
};

export default SessionDetailModal;
